import type { LatLng } from './mapProvider';

/**
 * The browser's own "where am I", wrapped so callers get one shape of failure.
 *
 * `navigator.geolocation` predates promises, reports errors through numeric
 * codes, and on a denied permission never calls back at all in some browsers
 * unless a timeout is set. Every one of those is a checkout that hangs with a
 * spinner, so they are all handled here.
 */

export type GeolocationFailure =
  /** They said no. Not an error — offer the map instead. */
  | 'denied'
  /** No fix: indoors, airplane mode, a desktop with no radio. */
  | 'unavailable'
  /** Took too long. The map is the fallback. */
  | 'timeout'
  /** The API is not there at all — an old browser, or an insecure origin. */
  | 'unsupported';

export interface Located {
  coordinates: LatLng;
  /** The device's own error estimate in metres, when it gave one. */
  accuracyM: number | null;
}

export class GeolocationError extends Error {
  constructor(readonly reason: GeolocationFailure) {
    super(reason);
    this.name = 'GeolocationError';
  }
}

/** What to tell the customer. Each one names the way forward, not the fault. */
export const GEOLOCATION_MESSAGES: Record<GeolocationFailure, string> = {
  denied: 'Your browser is blocking location access. You can move the map to your door instead.',
  unavailable:
    'Your device could not get a location just now. You can move the map to your door instead.',
  timeout: 'That took too long. You can move the map to your door instead.',
  unsupported: 'This browser cannot share a location. Move the map to your door instead.',
};

/**
 * Browsers only expose this on a secure origin, so on plain http it is absent
 * rather than denied. Checked separately so the message can say so.
 */
export function geolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

export function locateDevice(timeoutMs = 15_000): Promise<Located> {
  if (!geolocationAvailable()) {
    return Promise.reject(new GeolocationError('unsupported'));
  }

  return new Promise<Located>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          coordinates: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          // `accuracy` is always present per the spec, but a wrong number here
          // ends up on a delivery label, so an implausible one is dropped.
          accuracyM: Number.isFinite(position.coords.accuracy)
            ? Math.round(position.coords.accuracy)
            : null,
        }),
      (error) => reject(new GeolocationError(toFailure(error))),
      {
        // Worth the extra second and the extra battery: a coarse fix from a
        // wifi lookup can be a kilometre out, which is a different building.
        enableHighAccuracy: true,
        timeout: timeoutMs,
        // Never a cached fix. Someone setting a delivery address has usually
        // just arrived somewhere, and yesterday's position is the wrong answer
        // delivered confidently.
        maximumAge: 0,
      },
    );
  });
}

/**
 * Above this the "fix" is a neighbourhood, not a door.
 *
 * Not a rejection — it is still a better starting point than the shop's own
 * coordinates — but the customer is asked to nudge the map, because a 500m
 * error is the difference between their gate and the next street.
 */
export const COARSE_ACCURACY_M = 100;

function toFailure(error: GeolocationPositionError): GeolocationFailure {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'denied';
    case error.POSITION_UNAVAILABLE:
      return 'unavailable';
    case error.TIMEOUT:
      return 'timeout';
    default:
      return 'unavailable';
  }
}
